import { MatterUpdater } from '../core/modules/MatterUpdater';
import { ParallaxController } from '../core/modules/Parallax/ParallaxController';
import { ActionRunner } from '../core/modules/ActionRunner/ActionRunner';
import { ActionBlock } from '../core/modules/ActionRunner/ActionBlock';
import { LevelManager } from './LevelManager';
import { ChargeStage, Level, LevelStage, LootStage, RampStage, LevelName } from './level/levels/level';
import { LevelUtil } from './level/levels/LevelUtil';
import { ThemeUtil } from './level/themes/ThemeUtil';
import { RunSettings, RunSettingsHandler } from './RunSettingsHandler';
import { VehicleFactory } from './vehicle/VehicleFactory';
import { VehicleManager } from './vehicle/VehicleManager';
import { Theme, Weather } from './level/themes/theme';
import { AIBoss } from './vehicle/AIBoss';
import { Station } from './level/Station';
import { CameraBall } from './helpers/CameraBall';
import { app } from '../core/App';
import { Sounds } from '../sounds';

export interface RunManagerOptions {
  cameraIntroStartPointX?: number;
  cameraIntroStartPointY?: number;
  cameraOffset?: number;
  cameraBoundsX?: number;
  cameraBoundsY?: number;
  cameraBoundsWidth?: number;
  cameraBoundsHeight?: number;
}

export const RunPhase = {
  PanToVillain: 'panToVillain',
  PanToHero: 'panToHero',
  Main: 'main',
  Capture: 'capture'

  // PreIntro: 'preIntro',
  // PreVillainIntro: 'preVillainIntro',
  // VillainIntro: 'villainInto',
  // PreCameraPan: 'preCameraPan',
  // CameraPan: 'cameraPan',
  // PreHeroIntro: 'preIntroHero',
  // HeroIntro: 'heroIntro',
  // Run: 'run',
  // Capture: 'capture',
  // End: 'End'
} as const;
type RunPhaseKeys = keyof typeof RunPhase;
type RunPhaseValues = typeof RunPhase[RunPhaseKeys];

export class RunManager {
  public static events = {
    PlayerDeath: 'playerDeath',
    WorldSwitchStarted: 'worldSwitchStarted',
    WorldSwitchEnded: 'worldSwitchEnded',
    RunStarted: 'runStarted',
    RunOver: 'runOver'
  };

  protected scene: Phaser.Scene;
  protected isInitialised: boolean;
  protected levelManager: LevelManager;
  protected parallaxController: ParallaxController;
  protected matterUpdater: MatterUpdater;
  protected camera: Phaser.Cameras.Scene2D.Camera;
  protected vehicleLayer: Phaser.GameObjects.Layer;
  protected gameUILayer: Phaser.GameObjects.Layer;
  protected vehicleManager: VehicleManager;
  protected vehicleFactory: VehicleFactory;
  protected options: Required<RunManagerOptions>;

  protected runSettingsHandler: RunSettingsHandler | undefined;
  protected level: Level | undefined;
  protected theme: Theme | undefined;

  protected instantRestart: boolean;
  protected regenOnRestart: boolean;

  protected hasStartedRun: boolean;
  protected isRunning: boolean;

  protected cameraBall: CameraBall;

  protected phaseRunner: ActionRunner;
  protected runPhases: ActionBlock[];

  constructor(
    scene: Phaser.Scene,
    levelManager: LevelManager,
    parallaxController: ParallaxController,
    matterUpdater: MatterUpdater,
    camera: Phaser.Cameras.Scene2D.Camera,
    vehicleLayer: Phaser.GameObjects.Layer,
    gameUILayer: Phaser.GameObjects.Layer,
    options: RunManagerOptions = {}
  ) {
    this.scene = scene;
    this.levelManager = levelManager;
    this.parallaxController = parallaxController;
    this.matterUpdater = matterUpdater;
    this.camera = camera;
    this.vehicleLayer = vehicleLayer;
    this.gameUILayer = gameUILayer;

    this.vehicleManager = new VehicleManager(this.scene, this.levelManager);
    this.vehicleFactory = new VehicleFactory(this.scene, this.levelManager, this.vehicleManager, this.matterUpdater, this.vehicleLayer, this.gameUILayer);

    let defaultOptions: Required<RunManagerOptions> = {
      cameraIntroStartPointX: 0,
      cameraIntroStartPointY: 0,
      cameraOffset: -400,
      cameraBoundsX: 0,
      cameraBoundsY: -384,
      cameraBoundsWidth: 1000,
      cameraBoundsHeight: 0
    };

    this.options = Object.assign({}, defaultOptions, options);

    this.instantRestart = true;
    this.regenOnRestart = true;
    this.isInitialised = false;

    this.hasStartedRun = false;
    this.isRunning = false;

    this.cameraBall = new CameraBall(this.scene, this.camera);
    this.cameraBall.x = 1920 / 2;

    this.phaseRunner = new ActionRunner();
    this.runPhases = [];

    this.scene.events.on(Phaser.Scenes.Events.UPDATE, this.update, this);
    // this.scene.events.on(RunManager.events.PlayerDeath, this.onPlayerDeath, this);
    this.scene.events.once(Phaser.Scenes.Events.SHUTDOWN, this.shutdown, this);
  }

  get Level() {
    return this.level;
  }

  get Theme() {
    return this.theme;
  }

  get VehicleManager(): VehicleManager {
    return this.vehicleManager;
  }

  get CameraBall(): CameraBall {
    return this.cameraBall;
  }

  public initRun(runSettings: RunSettings, level: Level, theme: Theme) {
    this.runSettingsHandler = new RunSettingsHandler(this.scene, runSettings);
    this.level = level;
    this.theme = theme;

    this.instantRestart = runSettings.instantRestart;
    this.regenOnRestart = runSettings.regenOnRestart;

    this.setupWorld(this.level);

    this.resetRun();
  }

  public resetRun() {
    this.hasStartedRun = false;
    this.isRunning = false;

    // let spawnPointX = this.level?.playerSpawnPoint.x ?? 0;
    // let spawnPointY = this.level?.playerSpawnPoint.y ?? 0;

    this.camera?.setBounds(
      this.options.cameraBoundsX,
      this.options.cameraBoundsY,
      (this.levelManager.LevelEndPointX ?? 0) + this.options.cameraBoundsWidth,
      this.options.cameraBoundsHeight
    );

    // this.player?.resetPosition(spawnPointX, spawnPointY);
  }

  public startRun() {
    this.hasStartedRun = true;
    this.isRunning = true;

    this.vehicleManager.start();

    this.phaseRunner.start();

    this.scene.events.emit(RunManager.events.RunStarted);
  }

  public pauseRun() {
    this.isRunning = false;

    this.vehicleManager.pause();
  }

  public resumeRun() {
    this.isRunning = true;

    this.vehicleManager.resume();
  }

  public endRun() {
    this.hasStartedRun = false;
    this.isRunning = false;

    this.vehicleManager.end();

    if (this.instantRestart && this.vehicleManager.HeroVehicle && this.level && this.theme) {
      if (this.regenOnRestart) {
        // this.levelManager?.endLevel();
        this.levelManager.setupLevel(this.theme.roadDetails.startPointX, this.theme.roadDetails.startPointY);
        // this.playerTaxi?.respawn(this.level?.playerSpawnPoint.x ?? 0, this.level?.playerSpawnPoint.y ?? 0);
        // this.camera?.startFollow(this.playerTaxi, true, 0.9, 0.5, -400, 0);
        this.scene.time.delayedCall(1000, () => this.startRun());
      } else {
        // this.levelManager?.resetLevel();
        // this.playerTaxi?.respawn(this.level?.playerSpawnPoint.x ?? 0, this.level?.playerSpawnPoint.y ?? 0);
        // this.camera?.startFollow(this.playerTaxi, true, 0.9, 0.5, -400, 0);
        this.scene.time.delayedCall(1000, () => this.startRun());
      }
    } else {
      this.levelManager?.endLevel();

      // Show endscreen.
      this.scene.events.emit(RunManager.events.RunOver);
    }
  }

  // public startPhaseIntro() {
  //   this.vehicleManager.startIntroPhase();
  //   this.runPhase = RunPhase.PreVillainIntro;
  // }

  // public startPhaseCapture() {
  //   this.vehicleManager.startCapturePhase();
  //   this.runPhase = RunPhase.Capture;

  //   this.scene.time.delayedCall(2000, () => {
  //     this.runPhase = RunPhase.End;
  //   });
  // }

  protected panToVillain(): ActionBlock {
    return {
      actionName: RunPhase.PanToVillain,
      onActivate: (runner, callback) => {
        this.vehicleManager.startVillain();
        this.vehicleManager.startHero();
        callback();
      },
      onUpdate: (runner, time, delta, callback) => {
        if (!this.vehicleManager.VillainVehicle) return false;

        if (this.vehicleManager.VillainVehicle.x >= this.cameraBall.x) {
          this.cameraBall.x = this.vehicleManager.VillainVehicle.x;
          this.cameraBall.toggleMoving(true);
          this.cameraBall.setSpeedX(this.vehicleManager.VillainVehicle.CurrentSpeedXPerMS);
          // this.cameraBall.panXToRelativePoint(0);

          // this.camera.startFollow(this.vehicleManager.VillainVehicle, true, 0.9, 0, 0, 0);

          this.scene.time.delayedCall(1000, () => {
            this.VehicleManager.VillainVehicle?.onVoice();
            // this.vehicleManager.VillainVehicle?.panByTimeToXRelativePointX(this.cameraBall.x, 900, 2000, Phaser.Math.Easing.Linear);
            this.vehicleManager.HeroVehicle?.panByTimeToXRelativePointX(this.cameraBall.x, -700, 2000, Phaser.Math.Easing.Linear);
            this.cameraBall.toggleMoving(false);
            callback();
          });

          return true;
        }

        return false;
      },
      OnDeactivate: (runner, callback) => {
        // this.camera.stopFollow();
        callback();
      }
    };
  }

  protected panToHero(): ActionBlock {
    let cameraPanSpeed = 5;
    let heroGap = 740;

    return {
      actionName: RunPhase.PanToHero,
      onActivate: (runner, callback) => {
        // this.vehicleManager.VillainVehicle?.panByTimeToXRelativePointX(this.cameraBall.x, 900, 2000, Phaser.Math.Easing.Linear);
        // this.vehicleManager.HeroVehicle?.panByTimeToXRelativePointX(this.cameraBall.x, -450, 2000, Phaser.Math.Easing.Linear);
        // this.cameraBall.panXToRelativePoint(450);
        callback();
      },
      onUpdate: (runner, time, delta, callback) => {
        if (!this.vehicleManager.HeroVehicle) return false;

        if (this.vehicleManager.HeroVehicle.x + heroGap >= this.cameraBall.x) {
          this.cameraBall.x = this.vehicleManager.HeroVehicle.x + heroGap;
          this.cameraBall.toggleMoving(true);
          this.cameraBall.setSpeedX(this.vehicleManager.HeroVehicle.CurrentSpeedXPerMS);
          // this.cameraBall.panXToRelativePoint(this.vehicleManager.HeroVehicle.x + heroGap);

          // Make sure all vehicles are in the correct position.
          this.scene.time.delayedCall(2000, () => {
            this.vehicleManager.VillainVehicle?.panByTimeToXRelativePointX(this.cameraBall.x, 350, 500, Phaser.Math.Easing.Linear);
            this.vehicleManager.HeroVehicle?.panByTimeToXRelativePointX(this.cameraBall.x, -350, 500, Phaser.Math.Easing.Linear);
          });

          this.scene.time.delayedCall(2500, () => {
            this.VehicleManager.HeroVehicle?.cheer();
            callback();
          });
          return true;
        }

        // if (!this.vehicleManager.VillainVehicle?.IsPanning && !this.vehicleManager.HeroVehicle?.IsPanning) {
        //   callback();
        //   return true;
        // }

        // if (!this.vehicleManager.HeroVehicle) return false;

        // let distanceAheadOfTarget = this.camera.worldView.centerX - (this.vehicleManager.HeroVehicle.x - this.options.cameraOffset);
        // let distanceToTarget = Math.abs(distanceAheadOfTarget);
        // if (distanceAheadOfTarget > 0 && distanceToTarget > cameraPanSpeed) {
        //   let progress = cameraPanSpeed / distanceToTarget;
        //   this.camera.centerOn(
        //     Phaser.Math.Interpolation.Linear([this.camera.worldView.centerX, this.vehicleManager.HeroVehicle.x - this.options.cameraOffset], progress),
        //     this.camera.y
        //   );
        // } else {
        //   this.camera.centerOn(this.vehicleManager.HeroVehicle.x - this.options.cameraOffset, this.camera.y);
        //   this.camera.startFollow(this.vehicleManager.HeroVehicle, true, 0.9, 0, this.options.cameraOffset, 0);
        //   callback();
        //   return true;
        // }

        return false;
      }
      // OnDeactivate: (runner, callback) => {
      //   this.scene.time.delayedCall(500, () => {
      //     callback();
      //   });
      // }
    };
  }

  protected mainPhase(): ActionBlock {
    return {
      actionName: RunPhase.Main,
      onActivate: (runner, callback) => {
        this.levelManager.startLevel();
        callback();
      },
      onUpdate: (runner, time, delta, callback) => {
        // this.levelManager.checkChargingStations(this.camera.worldView.right);
        this.levelManager.updateLevel(time, delta);

        if (!this.levelManager.IsLevelFinished) {
          callback();
          return true;
        }

        return false;

        // // Check end conditions.
        // if (RunSettingsHandler.hasCollectedAllCharges(this.scene)) {
        //   callback();
        //   return true;
        // }

        // return false;
      }
    };
  }

  protected capturePhase(): ActionBlock {
    return {
      actionName: RunPhase.Capture,
      onActivate: (runner, callback) => {
        this.VehicleManager.startCapturePhase();

        // TODO: Move into weapon configs.
        let distanceToClose = -275;
        let cameraDistanceToPan = 1000;
        if (this.Level?.id == LevelName.Level3) {
          distanceToClose = -300;
          cameraDistanceToPan = 975;
        }

        this.vehicleManager?.HeroVehicle?.panByTimeToXRelativePointX(this.cameraBall.x + 1000, distanceToClose, 3000, Phaser.Math.Easing.Linear);
        this.cameraBall.panXToRelativePoint(cameraDistanceToPan);
        // this.VehicleManager.HeroVehicle?.panBySpeedToRelativePointX(this.cameraBall.x, 250);
        // this.cameraBall.panXToRelativePoint(600);
        // this.VehicleManager.HeroVehicle?.panBySpeedToRelativePointX(this.cameraBall.x, -100);
        // this.cameraBall.panXToRelativePoint(350);

        // TODO: Move into weapon configs.
        // Force the hero either in front or behind the villain.
        let heroTargetYOffset = 0;
        if (this.Level?.id == LevelName.Level3) heroTargetYOffset = -2;

        let villainTargetYOffset = 0;
        if (this.Level?.id == LevelName.Level3) villainTargetYOffset = 0;

        let isInFront = false;
        if (this.Level?.id == LevelName.Level3) isInFront = true;

        this.scene.time.delayedCall(2000, () => {
          app.audioHandler.playSound(this.scene, Sounds.Track.ChargeComplete1, Sounds.Channel.SFX, { volume: 1.0 });
          this.vehicleManager.prepareCapture(isInFront, heroTargetYOffset, villainTargetYOffset);
        });

        this.scene.time.delayedCall(this.vehicleManager.HeroVehicle?.Weapon?.PrefireTime ?? 4000, () => {
          this.vehicleManager.HeroVehicle?.Weapon?.beforeCaptureFire();
        });

        this.scene.time.delayedCall(4000, () => {
          this.VehicleManager.fireCapture(heroTargetYOffset, villainTargetYOffset);

          if (this.VehicleManager.HeroVehicle?.Weapon) {
            this.scene.time.delayedCall(this.VehicleManager.HeroVehicle.Weapon.BeforeHitTime, () => {
              this.VehicleManager.HeroVehicle?.Weapon?.beforeCaptureHit();
            });
          }

          if (this.level?.id != LevelName.Level4) {
            this.scene.time.delayedCall(this.vehicleManager.HeroVehicle?.Weapon?.FireTime ?? 1000, () => {
              // if (this.VehicleManager.HeroVehicle != undefined) {
              //   app.audioHandler.playSound(this.scene, this.VehicleManager.HeroVehicle.sounds.hit, Sounds.Channel.SFX, { volume: 1.0 });
              // }

              this.vehicleManager.HeroVehicle?.Weapon?.onCaptureHit();
              this.vehicleManager.HeroVehicle?.Weapon?.onPull();
              this.VehicleManager.HeroVehicle?.CharacterSpine.heroTireScreech();
              this.VehicleManager.VillainVehicle?.CharacterSpine.villainPulled();
              this.VehicleManager.VillainVehicle?.CharacterSpine.villainTireScreech();
              this.VehicleManager.HeroVehicle?.stopEngineSFX();
              this.VehicleManager.VillainVehicle?.stopEngineSFX();

              if (this.VehicleManager.VillainVehicle != undefined) {
                app.audioHandler.playSound(this.scene, this.VehicleManager.VillainVehicle.sounds.villainVoice, Sounds.Channel.SFX, { volume: 1.0 });
              }

              this.vehicleManager.stopVillain();
              this.vehicleManager.stopHero();
              this.cameraBall.setTargetSpeedX(0, 0.055, 0.01);
              // this.cameraBall.toggleMoving(false);

              this.scene.time.delayedCall(3000, () => {
                this.cameraBall.toggleMoving(false);
                callback();
                this.endRun();
              });
            });
          } else {
            this.scene.time.delayedCall(1680, () => {
              // if (this.VehicleManager.HeroVehicle != undefined) {
              //   app.audioHandler.playSound(this.scene, this.VehicleManager.HeroVehicle.sounds.hit, Sounds.Channel.SFX, { volume: 1.0 });
              // }

              this.vehicleManager.HeroVehicle?.Weapon?.onCaptureHit();
              this.vehicleManager.HeroVehicle?.Weapon?.onPull();
              this.VehicleManager.HeroVehicle?.CharacterSpine.cancelFire();
              this.VehicleManager.VillainVehicle?.CharacterSpine.villainPulled();
              this.VehicleManager.VillainVehicle?.CharacterSpine.villainTireScreech();
              this.VehicleManager.VillainVehicle?.crash();
              this.VehicleManager.VillainVehicle?.stopEngineSFX();

              if (this.VehicleManager.VillainVehicle != undefined) {
                app.audioHandler.playSound(this.scene, this.VehicleManager.VillainVehicle.sounds.villainVoice, Sounds.Channel.SFX, { volume: 1.0 });
              }

              this.scene.time.delayedCall(1000, () => {
                this.VehicleManager.HeroVehicle?.cheer();
              });

              this.scene.time.delayedCall(3000, () => {
                this.cameraBall.toggleMoving(false);
                callback();
                this.endRun();
              });
            });
          }
        });
      }
    };
  }

  protected update(time: number, delta: number) {
    if (!this.hasStartedRun || !this.isRunning) return;

    this.refreshEndpoint();
    this.phaseRunner.update(time, delta);
  }

  protected setupWorld(newLevel: Level) {
    this.level = newLevel;
    this.theme = ThemeUtil.getTheme(this.level.theme);

    this.parallaxController?.clearLayers();
    this.parallaxController?.buildLayersFromData(
      this.theme.parallaxLayers.map((data) => {
        return { ...data };
      })
    );

    this.runPhases.push(this.panToVillain());
    this.runPhases.push(this.panToHero());
    this.runPhases.push(this.mainPhase());
    this.runPhases.push(this.capturePhase());
    this.phaseRunner.setActions(this.runPhases);

    this.levelManager.changeLevel(this.level, this.theme);
    this.levelManager.setupLevel(this.theme.roadDetails.startPointX, this.theme.roadDetails.startPointY);

    // this.player.resetPosition(this.world.playerSpawnPoint.x, this.world.playerSpawnPoint.y);

    // Spawn all vehicles.
    this.vehicleManager.init(this.vehicleFactory, this.level);

    if (this.cameraBall && this.camera) {
      this.camera.setScroll(this.cameraBall.x, this.cameraBall.y);
      this.camera.startFollow(this.cameraBall, true, 0.9, 0, 0, 0);
    }

    // if (this.vehicleManager.HeroVehicle && this.camera) {
    //   // Moving the camera instantly to avoid jarring lerp jump.
    //   this.camera.setScroll(this.vehicleManager.HeroVehicle.x - this.options.cameraOffset, this.vehicleManager.HeroVehicle.y);
    // }
  }

  protected refreshEndpoint() {
    if (this.levelManager.LevelEndPointX + 1000 <= this.camera.worldView.right) {
      this.levelManager.setEndpoint(this.levelManager.LevelEndPointX + 10000);
      this.camera?.setBounds(
        this.options.cameraBoundsX,
        this.options.cameraBoundsY,
        (this.levelManager.LevelEndPointX ?? 0) + this.options.cameraBoundsWidth,
        this.options.cameraBoundsHeight
      );
    }
  }

  protected shutdown() {
    this.scene.events.off(Phaser.Scenes.Events.UPDATE, this.update, this);
    // this.scene.events.off(RunManager.events.PlayerDeath, this.onPlayerDeath, this);
    // this.isInitialised = false;
  }
}
